import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function viewportBox() {
  const width = Math.min(
    window.innerWidth || Number.POSITIVE_INFINITY,
    document.documentElement?.clientWidth || Number.POSITIVE_INFINITY,
    window.visualViewport?.width || Number.POSITIVE_INFINITY
  );
  const height = Math.min(
    window.innerHeight || Number.POSITIVE_INFINITY,
    document.documentElement?.clientHeight || Number.POSITIVE_INFINITY,
    window.visualViewport?.height || Number.POSITIVE_INFINITY
  );
  return {
    left: 0,
    top: 0,
    width: Number.isFinite(width) ? width : window.innerWidth,
    height: Number.isFinite(height) ? height : window.innerHeight,
  };
}

/**
 * Info tip that keeps its bubble inside the visual viewport on narrow phones.
 */
export default function FieldTip({ text, label }) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const pointerKind = useRef('mouse');

  useEffect(() => {
    if (!show) return undefined;

    function dismissFromPointer(event) {
      if (btnRef.current?.contains(event.target)) return;
      pointerKind.current = 'mouse';
      setShow(false);
    }

    function dismissFromKey(event) {
      if (event.key === 'Escape') setShow(false);
    }

    document.addEventListener('pointerdown', dismissFromPointer, true);
    document.addEventListener('keydown', dismissFromKey);
    return () => {
      document.removeEventListener('pointerdown', dismissFromPointer, true);
      document.removeEventListener('keydown', dismissFromKey);
    };
  }, [show]);

  useLayoutEffect(() => {
    if (!show || !text) {
      setCoords(null);
      return undefined;
    }

    function placeBubble() {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const view = viewportBox();
      const margin = 10;
      const width = Math.min(280, view.width - margin * 2);
      const estimatedHeight = Math.min(160, Math.max(72, Math.ceil(String(text).length / 42) * 22 + 28));
      const btnCenter = r.left + r.width / 2;
      let left = btnCenter - width / 2;
      left = Math.max(view.left + margin, Math.min(left, view.left + view.width - width - margin));
      const caretLeft = Math.max(14, Math.min(width - 14, btnCenter - left));

      let top = r.bottom + 8;
      let placement = 'below';
      if (top + estimatedHeight > view.top + view.height - margin) {
        top = Math.max(view.top + margin, r.top - estimatedHeight - 8);
        placement = 'above';
      }

      setCoords({ left, top, width, placement, caretLeft });
    }

    placeBubble();
    window.addEventListener('resize', placeBubble);
    window.addEventListener('scroll', placeBubble, true);
    window.visualViewport?.addEventListener('resize', placeBubble);
    window.visualViewport?.addEventListener('scroll', placeBubble);
    return () => {
      window.removeEventListener('resize', placeBubble);
      window.removeEventListener('scroll', placeBubble, true);
      window.visualViewport?.removeEventListener('resize', placeBubble);
      window.visualViewport?.removeEventListener('scroll', placeBubble);
    };
  }, [show, text]);

  if (!text) return null;

  const bubble =
    show && coords && typeof document !== 'undefined'
      ? createPortal(
          <span
            className={`field-tip-bubble is-anchored is-${coords.placement}`}
            role="tooltip"
            style={{
              left: coords.left,
              top: coords.top,
              width: coords.width,
              ['--tip-caret-left']: `${coords.caretLeft}px`,
            }}
          >
            {text}
          </span>,
          document.body
        )
      : null;

  return (
    <span
      className="kanban-stage-tip field-tip"
      onPointerEnter={(event) => {
        pointerKind.current = event.pointerType || 'mouse';
        if (event.pointerType === 'touch') return;
        setShow(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        setShow(false);
      }}
      onFocus={() => {
        if (pointerKind.current === 'touch') return;
        setShow(true);
      }}
      onBlur={() => {
        pointerKind.current = 'mouse';
        setShow(false);
      }}
    >
      <button
        ref={btnRef}
        type="button"
        className="kanban-stage-tip-btn"
        aria-label={`${label}: ${text}`}
        tabIndex={0}
        onPointerDown={(event) => {
          pointerKind.current = event.pointerType || 'mouse';
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (pointerKind.current === 'touch') setShow((open) => !open);
          else setShow(true);
        }}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {bubble}
    </span>
  );
}
